/**
 * The thing that actually calls `mayAutoRotate()`.
 *
 * `rotation.ts` answers *may I rotate now*; this asks it, on a schedule, and
 * acts on the answer. Without it the predicate is dead code — which is exactly
 * what it was when Phase 5 was first declared finished: twenty passing tests and
 * no caller, so a key would ride all the way down to the 120-second floor, be
 * dropped, and the user would discover it as a ticket that had quietly changed
 * from `BUY UP` to `Enable trading`.
 *
 * ## Why a re-armed timeout and not an interval
 *
 * The safe window is `T+18…T+22` of a 60-second market — four seconds in sixty.
 * An interval either has to be short enough to land inside it (which means
 * waking ~60× per market to do nothing 56 times) or it drifts against the server
 * and misses the window entirely. Asking `msUntilNextRotationWindow()` for the
 * next boundary and sleeping exactly that long wakes **once per market at most**,
 * and only while a key is actually due — the common case is a key with six days
 * left, where this schedules nothing at all.
 *
 * The same shape as `Runtime#scheduleAnchorFollow`, and for the same reason: the
 * clock is corrected, so a timer re-derived from it each tick cannot drift.
 *
 * ## What it deliberately does not do
 *
 * It does not decide *whether* rotation is allowed — `mayAutoRotate()` owns
 * that, including the roll guard — and it does not touch the wallet itself. It
 * calls `onRotate` and gets out of the way, so the popup-raising code stays in
 * one place and this file is testable with no wagmi, no DOM and no clock.
 *
 * No React import: `wallet/rotation*.ts` is viem-free and React-free, which is
 * what keeps it off the anonymous cold load (invariant 13).
 */

import { mayAutoRotate, msUntilNextRotationWindow, keyState, type KeyTiming } from './rotation'

export interface RotationSchedulerOptions {
  /** The current (key, market, clock) tuple. Re-read on every tick. */
  getTiming: () => KeyTiming
  /**
   * Raise the wallet popup and re-register. Rejections are swallowed — a user
   * who declines is not asked again until the next safe window, which on a
   * 60-second market is a minute away and is the right amount of persistence.
   */
  onRotate: () => Promise<void>
  /**
   * True while something else already owns the wallet. Rotation must never
   * queue a second popup behind a sign-in the user is part-way through.
   */
  isBusy: () => boolean
  setTimeout?: (fn: () => void, ms: number) => number
  clearTimeout?: (handle: number) => void
}

/**
 * How long to wait before re-checking when there is nothing to do.
 *
 * A key with six days left needs no schedule at all, but the *state* that says
 * so changes with the clock, so something has to look again eventually. Ten
 * minutes is far below the ten-minute lead time it is watching for, so the
 * transition into `ROTATE_DUE` cannot be missed by more than one period.
 */
export const IDLE_RECHECK_MS = 600_000

/** Never sleep less than this — a zero-delay timer would spin. */
const MIN_DELAY_MS = 250

export class RotationScheduler {
  readonly #options: RotationSchedulerOptions
  readonly #setTimeout: (fn: () => void, ms: number) => number
  readonly #clearTimeout: (handle: number) => void
  #handle: number | null = null
  #running = false
  /** Guards against a second tick entering while `onRotate` is awaiting. */
  #rotating = false

  constructor(options: RotationSchedulerOptions) {
    this.#options = options
    this.#setTimeout =
      options.setTimeout ?? ((fn, ms) => globalThis.setTimeout(fn, ms) as unknown as number)
    this.#clearTimeout = options.clearTimeout ?? ((handle) => globalThis.clearTimeout(handle))
  }

  get running(): boolean {
    return this.#running
  }

  start(): void {
    if (this.#running) return
    this.#running = true
    this.#schedule()
  }

  stop(): void {
    this.#running = false
    if (this.#handle !== null) this.#clearTimeout(this.#handle)
    this.#handle = null
  }

  /**
   * Recompute the next wake-up now, discarding the one in flight.
   *
   * Necessary because the schedule is derived from state the scheduler does not
   * own. When there is nothing due it sleeps for `IDLE_RECHECK_MS`, and a key
   * that becomes due during that sleep would otherwise wait out the whole ten
   * minutes — which is *eventually* correct, since the recheck is shorter than
   * the lead time it watches for, but it means a user who has just armed keeps a
   * ten-minute-stale schedule, and it makes the behaviour untestable in anything
   * shorter than ten minutes.
   *
   * The caller wires this to the identity store, so a key appearing, expiring or
   * being revoked re-derives the timer immediately.
   */
  reschedule(): void {
    if (!this.#running) return
    this.#schedule()
  }

  /**
   * One evaluation. Exposed so tests can step it without a fake timer, and so a
   * caller can nudge it after something that changes the answer — an arm, a
   * revoke, a market boundary.
   */
  async tick(): Promise<void> {
    if (!this.#running || this.#rotating) return

    const timing = this.#options.getTiming()

    // Nothing to do for a healthy key, and nothing to do for a key that is
    // already gone — the second is the user's decision to make, not an ambush.
    if (keyState(timing) !== 'ROTATE_DUE') return
    if (this.#options.isBusy()) return
    if (!mayAutoRotate(timing)) return

    this.#rotating = true
    try {
      await this.#options.onRotate()
    } catch {
      // Declined, or the wallet failed. The next safe window is a minute away;
      // retrying inside this one would re-prompt a user who just said no.
    } finally {
      this.#rotating = false
    }
  }

  #schedule(): void {
    if (!this.#running) return
    if (this.#handle !== null) this.#clearTimeout(this.#handle)

    const timing = this.#options.getTiming()
    const delay =
      keyState(timing) === 'ROTATE_DUE' ?
        Math.max(MIN_DELAY_MS, msUntilNextRotationWindow(timing))
      : IDLE_RECHECK_MS

    this.#handle = this.#setTimeout(() => {
      void this.tick().finally(() => this.#schedule())
    }, delay)
  }
}
